import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2, Save, X, Plus, ExternalLink } from "lucide-react";

/**
 * Admin — Category Pages
 * Manages the editable landing page per route category (Cities, Dolomites, …).
 * Toggle `enabled` to show / hide the page and its entry in the "All Routes"
 * nav submenu on the public site.
 */
export default function RouteCategoriesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // full category doc being edited
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/route-categories`)
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Could not load categories"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleEnabled = async (c) => {
    try {
      await axios.put(`${API}/admin/route-categories/${c.slug}`, { enabled: !c.enabled });
      toast.success(`${c.name} ${!c.enabled ? "enabled" : "disabled"}`);
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category page "${c.name}"? Routes tagged with this category will stay untouched.`)) return;
    try {
      await axios.delete(`${API}/admin/route-categories/${c.slug}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-route-categories-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-white">Category Pages</h2>
          <p className="text-white/50 text-sm mt-1">
            One editable landing page per route category. Enabled pages appear in the
            &quot;All Routes&quot; navigation submenu.
          </p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          className="btn-gold"
          data-testid="admin-category-add-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add category page
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/50 py-10">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <p className="text-white/50 text-sm py-10">No category pages yet.</p>
      ) : (
        <div className="overflow-x-auto border border-white/5 rounded-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-[#111] text-white/60 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="px-4 py-3 text-left">Order</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Slug</th>
                <th className="px-4 py-3 text-left">Category filter</th>
                <th className="px-4 py-3 text-left">Enabled</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.slug} className="border-t border-white/5 text-white/80" data-testid={`admin-cat-row-${c.slug}`}>
                  <td className="px-4 py-3 font-mono text-white/50">{c.display_order}</td>
                  <td className="px-4 py-3">{c.name}</td>
                  <td className="px-4 py-3 font-mono text-white/50">{c.slug}</td>
                  <td className="px-4 py-3">{c.category}</td>
                  <td className="px-4 py-3">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!c.enabled}
                        onChange={() => toggleEnabled(c)}
                        data-testid={`admin-cat-toggle-${c.slug}`}
                      />
                      <span className={c.enabled ? "text-emerald-400" : "text-white/40"}>
                        {c.enabled ? "Enabled" : "Disabled"}
                      </span>
                    </label>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <a
                      href={`/routes/category/${c.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-white/50 hover:text-[#C5A059] mr-3 inline-flex items-center gap-1"
                      data-testid={`admin-cat-preview-${c.slug}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => setEditing(c)}
                      className="text-[#C5A059] hover:underline mr-3"
                      data-testid={`admin-cat-edit-${c.slug}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(c)}
                      className="text-red-400 hover:underline"
                      data-testid={`admin-cat-delete-${c.slug}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(editing || creating) && (
        <CategoryEditModal
          category={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function CategoryEditModal({ category, onClose, onSaved }) {
  const isNew = !category;
  const [form, setForm] = useState({
    slug: category?.slug || "",
    category: category?.category || "",
    name: category?.name || "",
    enabled: category?.enabled ?? true,
    display_order: category?.display_order ?? 99,
    featured_order: category?.featured_order ?? 0,
    h1: category?.h1 || "",
    hero_image_url: category?.hero_image_url || "",
    intro_html: category?.intro_html || "",
    meta_title: category?.meta_title || "",
    meta_description: category?.meta_description || "",
    keywords: category?.keywords || "",
    tailor_made_enabled: category?.tailor_made_enabled ?? true,
    tailor_made_headline: category?.tailor_made_headline || "",
    button1_label: category?.button1_label || "",
    button1_content: category?.button1_content || "",
    button2_label: category?.button2_label || "",
    button2_content: category?.button2_content || "",
  });
  const [saving, setSaving] = useState(false);

  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.slug.trim() || !form.category.trim()) {
      toast.error("Slug, category filter and display name are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        display_order: parseInt(form.display_order, 10) || 99,
        featured_order: Math.max(0, parseInt(form.featured_order, 10) || 0),
      };
      if (isNew) {
        await axios.post(`${API}/admin/route-categories`, payload);
        toast.success("Category page created");
      } else {
        await axios.put(`${API}/admin/route-categories/${category.slug}`, payload);
        toast.success("Saved");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10"
      onClick={onClose}
      data-testid="admin-cat-edit-modal"
    >
      <div
        className="bg-[#0F0F0F] border border-[#C5A059]/30 rounded-sm w-full max-w-3xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <h3 className="text-lg font-serif text-white">
            {isNew ? "New category page" : `Edit — ${category?.name}`}
          </h3>
          <button className="text-white/50 hover:text-white" onClick={onClose} data-testid="admin-cat-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-white/60 text-xs uppercase tracking-widest">Display name *</label>
              <Input value={form.name} onChange={(e) => u("name", e.target.value)} className="input-dark mt-1" data-testid="admin-cat-name" />
            </div>
            <div>
              <label className="text-white/60 text-xs uppercase tracking-widest">Slug (URL) *</label>
              <Input value={form.slug} onChange={(e) => u("slug", e.target.value.toLowerCase())} className="input-dark mt-1" data-testid="admin-cat-slug" />
            </div>
            <div>
              <label className="text-white/60 text-xs uppercase tracking-widest">Category filter (matches routes.category) *</label>
              <Input value={form.category} onChange={(e) => u("category", e.target.value)} className="input-dark mt-1" data-testid="admin-cat-category" />
            </div>
            <div>
              <label className="text-white/60 text-xs uppercase tracking-widest">Display order</label>
              <Input type="number" value={form.display_order} onChange={(e) => u("display_order", e.target.value)} className="input-dark mt-1" data-testid="admin-cat-order" />
            </div>
            <div>
              <label className="text-white/60 text-xs uppercase tracking-widest">Featured on homepage (0 = off, 1–3 = position)</label>
              <Input
                type="number"
                min="0"
                max="3"
                value={form.featured_order}
                onChange={(e) => u("featured_order", e.target.value)}
                placeholder="0"
                className="input-dark mt-1"
                data-testid="admin-cat-featured-order"
              />
            </div>
          </div>

          <div>
            <label className="text-white/60 text-xs uppercase tracking-widest">H1 heading</label>
            <Input value={form.h1} onChange={(e) => u("h1", e.target.value)} className="input-dark mt-1" data-testid="admin-cat-h1" />
          </div>
          <div>
            <label className="text-white/60 text-xs uppercase tracking-widest">Hero image URL</label>
            <Input value={form.hero_image_url} onChange={(e) => u("hero_image_url", e.target.value)} placeholder="/images/category-hero.jpg or https://…" className="input-dark mt-1" data-testid="admin-cat-hero" />
          </div>
          <div>
            <label className="text-white/60 text-xs uppercase tracking-widest">Intro copy (HTML / markdown)</label>
            <textarea
              value={form.intro_html}
              onChange={(e) => u("intro_html", e.target.value)}
              className="input-dark w-full min-h-[140px] p-3 rounded-sm mt-1"
              data-testid="admin-cat-intro"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-white/60 text-xs uppercase tracking-widest">Meta title (SEO)</label>
              <Input value={form.meta_title} onChange={(e) => u("meta_title", e.target.value)} className="input-dark mt-1" data-testid="admin-cat-meta-title" />
            </div>
            <div>
              <label className="text-white/60 text-xs uppercase tracking-widest">Keywords (comma-separated)</label>
              <Input value={form.keywords} onChange={(e) => u("keywords", e.target.value)} className="input-dark mt-1" data-testid="admin-cat-keywords" />
            </div>
          </div>
          <div>
            <label className="text-white/60 text-xs uppercase tracking-widest">Meta description (SEO)</label>
            <textarea
              value={form.meta_description}
              onChange={(e) => u("meta_description", e.target.value)}
              className="input-dark w-full min-h-[80px] p-3 rounded-sm mt-1"
              maxLength={320}
              data-testid="admin-cat-meta-desc"
            />
          </div>

          <div className="border-t border-white/5 pt-4 space-y-3">
            <p className="text-white/50 text-xs uppercase tracking-widest">
              Custom info buttons (shown between title and route list)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-white/60 text-xs">Button 1 label</label>
                <Input
                  value={form.button1_label}
                  onChange={(e) => u("button1_label", e.target.value)}
                  className="input-dark mt-1"
                  data-testid="admin-cat-btn1-label"
                />
                <textarea
                  value={form.button1_content}
                  onChange={(e) => u("button1_content", e.target.value)}
                  placeholder="Modal content (HTML or markdown)"
                  className="input-dark w-full min-h-[90px] p-3 rounded-sm mt-2"
                  data-testid="admin-cat-btn1-content"
                />
              </div>
              <div>
                <label className="text-white/60 text-xs">Button 2 label</label>
                <Input
                  value={form.button2_label}
                  onChange={(e) => u("button2_label", e.target.value)}
                  className="input-dark mt-1"
                  data-testid="admin-cat-btn2-label"
                />
                <textarea
                  value={form.button2_content}
                  onChange={(e) => u("button2_content", e.target.value)}
                  placeholder="Modal content (HTML or markdown)"
                  className="input-dark w-full min-h-[90px] p-3 rounded-sm mt-2"
                  data-testid="admin-cat-btn2-content"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-white/5 pt-4 space-y-3">
            <label className="inline-flex items-center gap-2 text-white/80">
              <input
                type="checkbox"
                checked={form.tailor_made_enabled}
                onChange={(e) => u("tailor_made_enabled", e.target.checked)}
                data-testid="admin-cat-tailor-toggle"
              />
              Show &quot;Request tailor-made transfer&quot; form on this page
            </label>
            {form.tailor_made_enabled && (
              <Input
                value={form.tailor_made_headline}
                onChange={(e) => u("tailor_made_headline", e.target.value)}
                placeholder="Optional custom form heading"
                className="input-dark"
                data-testid="admin-cat-tailor-headline"
              />
            )}
            <label className="inline-flex items-center gap-2 text-white/80">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => u("enabled", e.target.checked)}
                data-testid="admin-cat-enabled"
              />
              Enabled (visible on public site + in nav submenu)
            </label>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-white/5 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} className="text-white/60">
            Cancel
          </Button>
          <Button className="btn-gold" onClick={save} disabled={saving} data-testid="admin-cat-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {isNew ? "Create page" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
